import { CSSProperties, memo } from 'react'
import classNames from 'classnames'

import { echoPointForRow } from '@aytm/app/stats/page/question-views/components/charts/highcharts/lib/pointHalo'

import { TableDataView, TableDataViewCol, TableDataViewRow } from '@aytm/app/stats/components/data-views/table/types'

import Table from '@aytm/app/stats/components/data-views/table/components/Table'
import TableCellTitle from '@aytm/app/stats/components/data-views/table/components/cells/TableCellTitle'
import TableCellValue from '@aytm/app/stats/components/data-views/table/components/cells/TableCellValue'
import TableCellProgress from '@aytm/app/stats/components/data-views/table/components/cells/TableCellProgress'

// styles
import style from '@aytm/app/stats/components/data-views/table/styles/Table.module.scss'

type TableRowProps = {
  cols: TableDataViewCol[]
  row: TableDataViewRow
  swatchRun?: 'single' | 'start' | 'mid' | 'end'
  swatchPaint?: { className?: string; color?: string }
}

// A nested table is a breakdown of the row it hangs under, so it shows the same figures at the
// same precision. Builders name that precision on the parent's progress column and, being separate
// per question type, have never all remembered to repeat it on the child's -- leaving the cell to
// fall back to one decimal whatever the reader chose. The parent's answer is inherited here, once,
// rather than in every builder: a child that names its own still wins.
const percentDecimalsOf = (cols: TableDataViewCol[]): number | undefined => {
  for (const col of cols) {
    const found = col.cols ? percentDecimalsOf(col.cols) : 'progress' == col.type ? col.percentDecimals : undefined
    if (undefined !== found) return found
  }

  return undefined
}

const inheritPercentDecimals = (table: TableDataView, parentCols: TableDataViewCol[]): TableDataView => {
  const inherited = percentDecimalsOf(parentCols)
  if (undefined === inherited) return table

  const cols = table.cols.map(col =>
    'progress' == col.type && undefined === col.percentDecimals ? { ...col, percentDecimals: inherited } : col
  )

  return cols.some((col, index) => col !== table.cols[index]) ? { ...table, cols } : table
}

export default memo(TableRow)
export function TableRow(props: TableRowProps) {
  const renderCell = (cell: TableDataViewCol, index: number) => {
    switch (cell.type) {
      case 'title': {
        return (
          <TableCellTitle
            key={`table-row-cell-${index + 1}`}
            cell={props.row.cells[index]}
            width={'width' in cell ? cell.width : null}
            position={props.row.position}
          />
        )
      }
      case 'value': {
        return (
          <TableCellValue
            key={`table-row-cell-${index + 1}`}
            cell={props.row.cells[index]}
            width={'width' in cell ? cell.width : null}
            position={props.row.position}
            format={'format' in cell ? cell.format : null}
          />
        )
      }
      case 'progress': {
        return (
          <TableCellProgress
            key={`table-row-cell-${index + 1}`}
            cell={props.row.cells[index]}
            width={'width' in cell ? cell.width : null}
            percentDecimals={'percentDecimals' in cell ? cell.percentDecimals : null}
            percentSign={'percentSign' in cell ? cell.percentSign : null}
            format={'format' in cell ? cell.format : null}
          />
        )
      }
      default: {
        return <></>
      }
    }
  }

  const pointId = props.row.pointId ?? (null == props.row.position ? null : `a${props.row.position}`)

  return 'data' == props.row.type || !('type' in props.row) ? (
    <>
      <div
        data-row-position={props.row.position}
        data-point-id={props.row.pointId}
        className={classNames(
          style.row,
          { [style['row-disabled']]: 'disabled' in props.row && props.row.disabled },
          // where this row sits in a run of same-coloured neighbours, and the colour itself -- the
          // thread a run draws belongs to the row, so the row is what has to know the colour
          props.swatchRun ? style[`row-run-${props.swatchRun}`] : null,
          props.swatchPaint?.className
        )}
        style={props.swatchPaint?.color ? ({ '--stats-mark': props.swatchPaint.color } as CSSProperties) : undefined}
        onMouseEnter={event => echoPointForRow(event.currentTarget, pointId)}
        onMouseLeave={event => echoPointForRow(event.currentTarget, null)}
      >
        {props.cols.map((cell, index) =>
          'cols' in cell ? cell.cols.map((_cell, _index) => renderCell(_cell, index + _index)) : renderCell(cell, index)
        )}
      </div>
      {'table' in props.row ? <Table {...inheritPercentDecimals(props.row.table, props.cols)} /> : null}
    </>
  ) : 'type' in props.row && 'gap' == props.row.type ? (
    <div className={classNames(style.row, style['row-gap'])}></div>
  ) : null
}
